import React from "react";
import { Button } from "./ui/button";
import { RefreshCw } from "lucide-react";

/**
 * Shared loading + error fallback used by pages that fetch from /api on mount.
 *
 * Use:
 *   const [data, setData] = useState(null);
 *   const [error, setError] = useState(null);
 *   const load = useCallback(async () => { ... }, [deps]);
 *   useEffect(() => { load(); }, [load]);
 *
 *   if (!data || error) return <LoadingState error={error} onRetry={load} rows={5} />;
 *
 * Why: testers reported pages stuck on "Loading…" forever when the production
 * API briefly hiccupped — no retry button, no error message. This component
 * gives every page a uniform "skeleton OR error+retry" experience.
 */
export default function LoadingState({ error, onRetry, rows = 6, testid = "loading-state" }) {
  if (error) {
    return (
      <div data-testid={`${testid}-error`} className="p-6 rounded-3xl bg-white border border-[#E6E2D8] text-center">
        <p className="text-sm text-[#A0454E] mb-3">Couldn't load this page.</p>
        <p className="text-xs text-[#2C332A]/60 mb-4">{typeof error === "string" ? error : "Tap Try again to retry."}</p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline" className="rounded-full" data-testid={`${testid}-retry`}>
            <RefreshCw size={14} strokeWidth={1.6} className="mr-1.5" />
            Try again
          </Button>
        )}
      </div>
    );
  }
  return (
    <div data-testid={`${testid}-skeleton`} className="space-y-3 animate-pulse">
      <div className="h-6 w-32 rounded bg-[#E6E2D8]" />
      <div className="h-10 w-3/4 rounded bg-[#E6E2D8] mb-4" />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-16 rounded-2xl bg-[#EFEBE0]" />
      ))}
    </div>
  );
}
